import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { IonModal } from '@ionic/angular/standalone';
import { filter, fromEvent } from 'rxjs';

import { DeviceService } from '@core/device';
import { I18nPipe } from '@core/i18n';
import {
  isNotificationsKind,
  type Notifications,
  type NotificationsKind,
  NotificationsStore,
} from '@core/notifications';

import { ButtonComponent } from '@shared/button';
import { IconComponent, type IconName } from '@shared/icon';
import { MenuComponent, MenuTriggerDirective } from '@shared/menu';
import { SKELETON_ROWS, SkeletonComponent } from '@shared/skeleton';
import { TimeAgoPipe } from '@shared/time-ago';
import { ToggleGroupComponent, type ToggleOption } from '@shared/toggle-group';

import {
  fillMobileSkeleton,
  NOTIFICATIONS_DEFAULT_TAB,
  NOTIFICATIONS_HAS_UNREAD,
  NOTIFICATIONS_MESSAGE_SKELETON,
  NOTIFICATIONS_SKELETON,
} from './notifications.types';
import { notificationsCopy } from './notifications.util';

@Component({
  selector: 'ml-notifications',
  imports: [
    ButtonComponent,
    I18nPipe,
    IconComponent,
    IonModal,
    MenuComponent,
    MenuTriggerDirective,
    NgTemplateOutlet,
    ReactiveFormsModule,
    SkeletonComponent,
    TimeAgoPipe,
    ToggleGroupComponent,
  ],
  host: { class: 'contents' },
  templateUrl: './notifications.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsComponent {
  readonly kind = input.required<NotificationsKind>();

  protected readonly device = inject(DeviceService);
  private readonly notificationsStore = inject(NotificationsStore);

  protected readonly notifications = this.notificationsStore.notifications;
  protected readonly notificationsLoading = this.notificationsStore.loading;

  protected readonly toggleGroupOptions: ToggleOption[] = [
    { value: 'notifications', label: 'header.notifications.title' },
    { value: 'messages', label: 'header.messages.title' },
  ];
  protected readonly notificationsTab = new FormControl<string | null>(NOTIFICATIONS_DEFAULT_TAB);
  protected readonly menuOpen = signal(false);
  protected readonly modalOpen = signal(false);
  protected readonly selectedTab = signal<NotificationsKind>(NOTIFICATIONS_DEFAULT_TAB);
  protected readonly skeletonRows = SKELETON_ROWS;
  protected readonly notificationSkeleton = fillMobileSkeleton(NOTIFICATIONS_SKELETON);
  protected readonly messageSkeleton = fillMobileSkeleton(NOTIFICATIONS_MESSAGE_SKELETON);

  protected readonly isIos = this.device.isIos;
  protected readonly unreadItems = this.notificationsStore.unreadItems;

  protected readonly earlierItems = this.notificationsStore.earlierItems;

  protected readonly listedNotifications = this.notificationsStore.listedNotifications;

  protected readonly unreadCount = this.notificationsStore.unreadCount;

  protected readonly activeKind = computed(() =>
    this.device.isMobileUI() ? this.selectedTab() : this.kind(),
  );

  protected readonly copy = computed(() => notificationsCopy(this.activeKind()));

  protected readonly triggerIcon = computed<IconName>(() => {
    if (this.device.isMobileUI()) return 'inbox';
    if (this.kind() === 'notifications') return 'bell';
    return 'message';
  });
  protected readonly ariaKey = computed(() =>
    this.kind() === 'notifications' ? 'header.notificationsAria' : 'header.chatAria',
  );
  protected readonly showTriggerDot = computed((): boolean => {
    const items = this.notifications();
    const hasNotifications =
      items.length > 0
        ? items.some((item) => !item.opened)
        : NOTIFICATIONS_HAS_UNREAD.notifications;
    const hasMessages = NOTIFICATIONS_HAS_UNREAD.messages;
    if (this.device.isMobileUI()) return hasNotifications || hasMessages;
    return this.kind() === 'notifications' && hasNotifications;
  });
  protected readonly showEmpty = computed(() => {
    if (this.activeKind() === 'notifications') return this.notifications().length === 0;
    return true;
  });
  protected readonly showMarkAllRead = computed(
    () =>
      this.activeKind() === 'notifications' &&
      !this.notificationsLoading() &&
      this.unreadCount() > 0,
  );

  constructor() {
    this.notificationsTab.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      this.onTabChange(value);
    });

    fromEvent<PointerEvent>(document, 'pointerdown')
      .pipe(takeUntilDestroyed())
      .subscribe((event) => {
        if (!this.menuOpen()) return;
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (target.closest(`[ngpMenu], .header-notifications-trigger-${this.kind()}`)) return;
        this.menuOpen.set(false);
      });

    fromEvent<KeyboardEvent>(document, 'keydown')
      .pipe(
        filter((event) => event.key === 'Escape'),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.menuOpen.set(false));
  }

  protected onTriggerClick(): void {
    this.menuOpen.update((open) => !open);
    if (this.menuOpen()) this.notificationsStore.loadNotifications(this.activeKind());
  }

  protected onOpenModal(): void {
    this.selectedTab.set(NOTIFICATIONS_DEFAULT_TAB);
    this.notificationsTab.setValue(NOTIFICATIONS_DEFAULT_TAB);
    this.modalOpen.set(true);
    this.notificationsStore.loadNotifications(this.activeKind());
  }

  protected onModalDismiss(): void {
    this.modalOpen.set(false);
  }

  protected closeModal(): void {
    this.modalOpen.set(false);
  }

  protected onTabChange(value: string | null): void {
    if (!isNotificationsKind(value) || value === this.selectedTab()) return;

    this.selectedTab.set(value);
    this.notificationsStore.loadNotifications(value);
  }

  protected onNotificationClick(item: Notifications): void {
    if (item.opened) return;
    this.notificationsStore.markNotificationsOpened([item.id]);
  }

  protected onMarkAllRead(): void {
    const unreadIds = this.unreadItems().map((item) => item.id);
    if (!unreadIds.length) return;
    this.notificationsStore.markNotificationsOpened(unreadIds);
  }
}
